/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Page: Callback Page.
 */

import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { PageLoader } from "../components/PageLoader";
import { ROUTES } from "../config/routes";
import UserService from "../services/UserService";
import {
  hasActivePaidSubscription,
  logoutToShop,
  waitForPaidSubscription,
} from "../utils/subscriptionGate";

export const CallbackPage = () => {
  const {
    error,
    isAuthenticated,
    user,
    loginWithRedirect,
    isLoading,
    getAccessTokenSilently,
    logout,
  } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      if (
        error.error === "invalid_request" &&
        error.error_description?.toLowerCase().includes("signup is disabled")
      ) {
        navigate(ROUTES.HOME, { replace: true });
        return;
      }
    }

    if (isLoading) {
      return;
    }

    if (!isAuthenticated && !error) {
      loginWithRedirect({
        appState: { returnTo: ROUTES.CALLBACK },
      });
      return;
    }

    if (isAuthenticated && user) {
      let cancelled = false;

      (async () => {
        try {
          const token = await getAccessTokenSilently();
          await UserService.verify(user, token);

          const sub = await waitForPaidSubscription(() =>
            UserService.getUserSubscription(token, user, true)
          );
          if (cancelled) {
            return;
          }

          if (hasActivePaidSubscription(sub)) {
            navigate(ROUTES.DASHBOARD, { replace: true });
            return;
          }

          logoutToShop(logout);
        } catch (e) {
          console.error(
            "CallbackPage: Error during verify/subscription check:",
            e
          );
          if (!cancelled) {
            logoutToShop(logout);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [
    isAuthenticated,
    isLoading,
    error,
    user,
    loginWithRedirect,
    navigate,
    getAccessTokenSilently,
    logout,
  ]);

  return (
    <PageLayout>
      <PageLoader />
    </PageLayout>
  );
};
